import React, { useState, useEffect } from 'react';
import PropertyCard from '@/components/PropertyCard';
import InteractiveMap from '@/components/InteractiveMap';
import QuickSearch from '@/components/QuickSearch';
import { properties as initialProperties } from '@/data/properties';
import { Button } from '@/components/ui/button';
import { ArrowDownUp, LayoutGrid, Map as MapIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Properties = () => {
    const [allProperties, setAllProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [filters, setFilters] = useState({
        zone: 'all',
        type: 'all',
        category: 'all',
        bedrooms: 'all'
    });
    const [sortOrder, setSortOrder] = useState('default');
    const [viewMode, setViewMode] = useState('grid');

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                // Fetch from API
                const response = await fetch('/api.php?action=inmobiliario_properties');
                let apiProperties = [];

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && Array.isArray(data.properties)) {
                        apiProperties = data.properties.map(p => ({
                            id: Number(p.id),
                            titulo: p.title || p.titulo,
                            tipo: p.type || p.tipo,
                            operacion: p.operation || p.operacion,
                            estado: p.status || p.estado,
                            ubicacion: {
                                zona: p.zone || p.ubicacion?.zona || 'No definida',
                                ciudad: p.city || p.ubicacion?.ciudad || 'San Salvador',
                                latitud: Number(p.latitud) || 0,
                                longitud: Number(p.longitud) || 0
                            },
                            dimensiones: {
                                habitaciones: Number(p.bedrooms) || Number(p.dimensiones?.habitaciones) || 0,
                                banios: Number(p.bathrooms) || Number(p.dimensiones?.banios) || 0,
                                metrosCuadrados: Number(p.area) || Number(p.dimensiones?.metrosCuadrados) || 0
                            },
                            precio: {
                                monto: Number(p.price) || Number(p.precio?.monto) || 0
                            },
                            multimedia: {
                                fotos: p.images ? p.images.map(img => ({ url: typeof img === 'string' ? img : img.url })) : [{ url: p.image_url || "https://via.placeholder.com/800x600?text=No+Image" }]
                            },
                            esNueva: Boolean(p.is_new),
                            esTrending: Boolean(p.is_trending),
                            vistas: Number(p.views) || 0,
                            agente: p.agent || null
                        }));
                    }
                }

                // Merge with initial mock properties (preserving mock IDs if they don't collide, 
                // but real app should probably rely on API. For safety/stability, we keep initialProperties as fallback/demo)
                // Filter out any API properties that might duplicate initial ones by ID if IDs overlap?
                // Assuming API IDs and Mock IDs are distinct or we prefer API.
                // Let's just append API properties to initialProperties for now, or use API if available.
                // User said "Sincronización de Propiedades". This implies dynamic data is key.

                const combined = [...initialProperties, ...apiProperties];
                // Remove duplicates by ID just in case
                const unique = Array.from(new Map(combined.map(item => [item.id, item])).values());

                setAllProperties(unique);
                setFilteredProperties(unique);
            } catch (err) {
                console.error("Failed to fetch properties:", err);
                // Fallback to initialProperties only
                setAllProperties(initialProperties);
                setFilteredProperties(initialProperties);
            }
        };

        fetchProperties();
    }, []);

    const handleSearch = (newFilters, newSortOrder) => {
        setFilters(newFilters);
        setSortOrder(newSortOrder);
        applyFilters(newFilters, newSortOrder, allProperties);
    };

    const applyFilters = (currentFilters, currentSort, propertiesToFilter) => {
        let result = [...propertiesToFilter];

        // Filter by Zone
        if (currentFilters.zone !== 'all') {
            result = result.filter(p => p.ubicacion.zona === currentFilters.zone);
        }

        // Filter by Type (Venta/Alquiler)
        if (currentFilters.type !== 'all') {
            result = result.filter(p => p.operacion === currentFilters.type);
        }

        // Filter by Category (Casa, Apartamento, etc)
        if (currentFilters.category !== 'all') {
            result = result.filter(p => p.tipo === currentFilters.category);
        }

        // Filter by Bedrooms
        if (currentFilters.bedrooms !== 'all') {
            const minBedrooms = parseInt(currentFilters.bedrooms);
            result = result.filter(p => p.dimensiones.habitaciones >= minBedrooms);
        }

        // Sort
        if (currentSort === 'price-asc') {
            result.sort((a, b) => a.precio.monto - b.precio.monto);
        } else if (currentSort === 'price-desc') {
            result.sort((a, b) => b.precio.monto - a.precio.monto);
        } else if (currentSort === 'recent') {
            // Assuming higher ID is more recent for this mock
            result.sort((a, b) => b.id - a.id);
        }

        setFilteredProperties(result);
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-16">
            <div className="container mx-auto px-4">
                <div className="mb-8 text-center">
                    <h1 className="text-4xl font-bold text-primary mb-4">Encuentra tu Propiedad Ideal</h1>

                    {/* Search & Filter Section - Moved below title */}
                    <div className="max-w-4xl mx-auto mt-6">
                        <QuickSearch onSearch={handleSearch} />
                    </div>

                    <p className="text-gray-600 max-w-2xl mx-auto mt-6">Explora nuestro catálogo completo de propiedades.</p>
                </div>

                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <p className="text-gray-600">
                        Mostrando <span className="font-bold text-primary">{filteredProperties.length}</span> propiedades
                    </p>

                    <div className="bg-white p-1 rounded-lg border shadow-sm flex gap-1">
                        <Button
                            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                            onClick={() => setViewMode('grid')}
                            size="sm"
                            className="text-sm"
                        >
                            <LayoutGrid className="w-4 h-4 mr-2" />
                            Ver por fichas
                        </Button>
                        <Button
                            variant={viewMode === 'map' ? 'secondary' : 'ghost'}
                            onClick={() => setViewMode('map')}
                            size="sm"
                            className="text-sm"
                        >
                            <MapIcon className="w-4 h-4 mr-2" />
                            Ver por mapa
                        </Button>
                    </div>
                </div>

                {viewMode === 'map' ? (
                    <div className="h-[calc(100vh-300px)] min-h-[500px] w-full rounded-xl overflow-hidden border shadow-sm relative z-0">
                        <InteractiveMap properties={filteredProperties} />
                    </div>
                ) : (
                    <>
                        {filteredProperties.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-xl shadow-sm">
                                <p className="text-xl text-gray-500">No se encontraron propiedades con estos filtros.</p>
                                <Button
                                    variant="link"
                                    onClick={() => handleSearch({ zone: 'all', type: 'all', category: 'all', bedrooms: 'all' }, 'default')}
                                    className="mt-2 text-secondary"
                                >
                                    Limpiar filtros
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredProperties.map(p => (
                                    <PropertyCard key={p.id} property={p} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Properties;
